import { Box, Button, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import TabCategoryActivity from './TabCategoryActivity';
import ArticlesAll from './ArticlesActivities';
import { Article, IGetAllArticle } from '@/interfaces/community/article';
import { GetAllArticleActivity } from '@/api/article';
import SkeletonTabs from '../../skeleton/SkeletonTabs';
import ArticleSkeletonCard from '../../skeleton/Article';
import { RootState } from '@/store/store';
import InfiniteScroll from 'react-infinite-scroll-component';
import useResponsiveItems from '@/hooks/useResponsiveItems';
import { restoreScrollPosition } from '@/utils/resstoreScrollPostion';

type Props = {
    title: string;
    articleType: number;
};
interface reqGetAllArticleActivity {
    reset?: boolean;
    id: number;
    offset?: number;
    limit?: number
}
type reqGetAllArticle = Omit<reqGetAllArticleActivity, "saveOffset">;
const CardArticleActivity: React.FC<Props> = ({ title, articleType }) => {

    const showLimit = useResponsiveItems({ xsCount: 4, smCount: 4, mdCount: 6, lgCount: 8, xlCount: 12, xxlCount: 12, });

    const tagId = useSelector((state: RootState) => state.articleActivity.tagId);
    const [articles, setArticles] = useState<IGetAllArticle>({ recordsTotal: 0, filterCount: 0, response: [] });
    const [offset, setOffset] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [allLoaded, setAllLoaded] = useState<boolean>(false);
    const [loadingPage, setLoadingPage] = useState<boolean>(true);
    const [recordsTotal, setRecordsTotal] = useState<number>(0);
    const [scrollY, setScrollY] = useState<number>(0);

    useLayoutEffect(() => {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }
        const handleScroll = () => {
            sessionStorage.setItem("scrollPosition", window.scrollY.toString());
        };

        window.addEventListener("scroll", handleScroll);

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);


    const fetchMoreData = () => {
        const storedOffset = Number(sessionStorage.getItem("offset")) || 0;
        const storedLimit = Number(sessionStorage.getItem("limit")) || 0;
        const newoffset = offset + showLimit;
        if (storedLimit > recordsTotal) {
            setAllLoaded(false);
        } else {
            const offsets = scrollY != 0 ? newoffset + storedOffset : newoffset;
            console.log('offsets:', offsets)
            setOffset(offsets);
            if (newoffset < recordsTotal) {
                setAllLoaded(false);

                const req: reqGetAllArticleActivity = {
                    reset: true,
                    id: tagId,
                    offset: offsets,
                    limit: showLimit
                };
                fetchArticles(req).then(() => restoreScrollPosition("scrollPosition"));;
            } else {
                setAllLoaded(false);
                const req: reqGetAllArticleActivity = {
                    reset: true,
                    id: tagId,
                    offset: offsets,
                    limit: showLimit
                };
                fetchArticles(req).then(() => restoreScrollPosition("scrollPosition"));;
            }
            sessionStorage.setItem("offset", offsets.toString());
        }
    }

    // ฟังก์ชันดึงข้อมูลบทความ
    const fetchArticles = async (req: reqGetAllArticleActivity) => {
        if (!req.reset && (loading || allLoaded)) return;
        setLoading(true);
        try {
            const response = await GetAllArticleActivity(
                req.limit || 0,
                req.offset || 0,
                req.id,
                articleType
            );

            if (response && !response.error) {
                setArticles((prev) => ({
                    ...prev,
                    response: [...prev.response, ...response.response],
                }));
                setOffset(req.offset || 0);
                setAllLoaded(response.response.length < showLimit);
                setRecordsTotal(response.recordsTotal);
                sessionStorage.setItem("limit", showLimit.toString());
            } else {
                setAllLoaded(true);
            }

            // Important: ปิด Skeleton เสมอ
            setLoadingPage(false);
        } catch (error) {
            console.error("Failed to load articles", error);
            setLoadingPage(false); // <= กันไว้ด้วย
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        const storedOffset = Number(sessionStorage.getItem("offset")) || 0;
        const storedLimit = Number(sessionStorage.getItem("limit")) || 0;
        const newLimit = storedOffset !== 0 ? storedOffset + storedLimit : showLimit;

        setScrollY(storedOffset);
        setLoadingPage(true);
        setOffset(0);
        setArticles({ recordsTotal: 0, filterCount: 0, response: [] });
        sessionStorage.removeItem("offset");
        sessionStorage.removeItem("limit");

        const delayDebounceFn = setTimeout(() => {
            const req: reqGetAllArticleActivity = {
                reset: true,
                id: tagId,
                offset: 0,
                limit: newLimit,
            };

            fetchArticles(req).then(() => {
                restoreScrollPosition("scrollPosition");
            });
        }, 100);

        return () => clearTimeout(delayDebounceFn);
    }, [articleType, showLimit, tagId]);

    return (
        <>
            <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                    <Typography variant="h4" align="left" className='txt-bold'>{title}</Typography>
                </Grid>
                <Grid item xs={12}>
                    <TabCategoryActivity />
                </Grid>
            </Grid>

            {!loadingPage ? (
                <>
                    {!loadingPage && articles.response.length === 0 ? (
                        <Grid item xs={12} sx={{ height: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Typography sx={{ color: "#292929", fontSize: "20px", textAlign: 'center' }}>
                                <Box component="span">
                                    ไม่มี{title}ที่ค้นหา
                                </Box>
                            </Typography>
                        </Grid>
                    ) : (
                        <InfiniteScroll
                            style={{ display: 'inline' }}
                            dataLength={articles.response.length}
                            next={fetchMoreData}
                            hasMore={!allLoaded}
                            loader={<ArticleSkeletonCard count={showLimit / 2} />}
                        // endMessage={<Typography variant="h6" mt={6} align="center">ทุก{title}ถูกโหลดแล้ว</Typography>}
                        >
                            <Grid container spacing={3} sx={{ mt: 0 }}>
                                {articles.response.map((article: Article, idx: number) => (
                                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2} xxl={2} key={article.id}>
                                        <ArticlesAll dataSet={article} searchTag={true} />
                                    </Grid>
                                ))}
                            </Grid>
                        </InfiniteScroll>
                    )}
                </>
            ) : (
                <>
                    <SkeletonTabs />
                    <ArticleSkeletonCard count={showLimit} />
                </>
            )}
        </>
    );
};

export default CardArticleActivity;
